import { useEffect, useRef } from 'react'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import TaxationRulesIcon from '../components/summary/TaxationRulesIcon'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import {
  TaxHarvestingScreenName,
  TaxHarvestingBottomSheetName,
  TaxHarvestingEventId,
  trackTaxHarvestingBottomSheetLoaded,
  trackTaxHarvestingUserClicked
} from '../tracking/taxHarvestingTracking'
import {
  TAXATION_RULES_HEADING_ID,
  TAX_GRANDFATHERING_RULE_HEADING_ID
} from './taxationRulesConstants'

type Props = {
  onDismiss: () => void
}

function GrandfatheringRuleSheet({ onDismiss }: { onDismiss: () => void }) {
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.TaxGrandfatheringRule)
  }, [])
  return (
    <BottomSheetContent
      heading='Tax Grandfathering rule'
      headingId={TAX_GRANDFATHERING_RULE_HEADING_ID}
      closeButtonAriaLabel='Close'
      closeButtonAriaDescription='double tap to close the dialog'
      onDismiss={onDismiss}
    >
      <div className='text-[14px] text-grey40 leading-[150%] space-y-3'>
        <p>
          For equity-oriented funds, gains made before 31 Jan 2018 on equity mutual funds aren&apos;t taxed.
        </p>
        <p>
          If you invested before this date, your cost price is taken as the higher of your actual purchase price or
          the NAV on 31 Jan 2018.
        </p>
        <p>So only gains after 31 Jan 2018 are taxable.</p>
      </div>
    </BottomSheetContent>
  )
}

export default function TaxationRulesBottomSheet({ onDismiss }: Props) {
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const grandfatheringTriggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.TaxationRules)
  }, [])

  const handleGrandfatheringDismiss = () => {
    closeNestedBottomSheet()
    window.setTimeout(() => grandfatheringTriggerRef.current?.focus(), 400)
  }

  const openGrandfatheringRuleSheet = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.TaxationRules,
      eventId: TaxHarvestingEventId.GrandfatheringRule
    })
    openNestedBottomSheet(
      <GrandfatheringRuleSheet onDismiss={handleGrandfatheringDismiss} />,
      handleGrandfatheringDismiss,
      {
        drawerTitleId: TAX_GRANDFATHERING_RULE_HEADING_ID,
        // TR_B06/B07 — no overlay dismiss stop in AT; Close button remains available
        dismissible: false
      }
    )
  }

  return (
    <BottomSheetContent
      logoSvg={<TaxationRulesIcon />}
      heading='Taxation rules'
      headingId={TAXATION_RULES_HEADING_ID}
      closeButtonAriaLabel='Close'
      closeButtonAriaDescription='double tap to close the dialog'
      onDismiss={onDismiss}
    >
      <div className='text-[14px] leading-[150%] space-y-4 pb-4'>
        <section className='space-y-1 border-b border-[#F0F4F7] pb-5' aria-labelledby='taxation-rules-equity-heading'>
          <h3 id='taxation-rules-equity-heading' className='text-[14px] font-[500] text-[#232323]'>
            1. Equity
          </h3>
          <h4 className='text-[14px] font-normal text-[#3D3D3D]'>Short Term Capital Gains (STCG)</h4>
          <p className='text-grey40'>20% tax on returns if redeemed before 1 year</p>
          <h4 className='text-[14px] font-normal text-[#3D3D3D] pt-2'>Long Term Capital Gains (LTCG)</h4>
          <ul className='list-disc pl-4 space-y-1 text-grey40'>
            <li>12.5% tax on returns if redeemed after 1 year</li>
            <li>LTCG up to ₹1.25L per year is exempt from tax.</li>
            <li>
              Gains made before 31 Jan 2018 are tax-exempt under the{' '}
              <button
                ref={grandfatheringTriggerRef}
                type='button'
                className='underline cursor-pointer text-grey40 bg-transparent border-0 p-0 font-inherit inline'
                aria-description='double tap to open the dialog'
                onClick={openGrandfatheringRuleSheet}
              >
                Grandfathering Rule
              </button>
              .
            </li>
          </ul>
        </section>

        <section className='space-y-1 border-b border-[#F0F4F7] pb-5' aria-labelledby='taxation-rules-non-equity-heading'>
          <h3 id='taxation-rules-non-equity-heading' className='text-[14px] font-[500] text-[#232323]'>
            2. Non-Equity
          </h3>
          <h4 className='text-[14px] font-normal text-[#3D3D3D]'>Short Term Capital Gains (STCG)</h4>
          <p className='text-grey40'>Taxed as per your income tax slab if redeemed within 2 years</p>
          <h4 className='text-[14px] font-normal text-[#3D3D3D] pt-2'>Long Term Capital Gains (LTCG)</h4>
          <p className='text-grey40'>Taxed at a flat 12.5% if redeemed after 2 years</p>
        </section>

        <section className='space-y-1 border-b border-[#F0F4F7] pb-5' aria-labelledby='taxation-rules-debt-heading'>
          <h3 id='taxation-rules-debt-heading' className='text-[14px] font-[500] text-[#232323]'>
            3. Debt
          </h3>
          <h4 className='text-[14px] font-normal text-[#3D3D3D]'>For units purchased before 1st Apr 2023</h4>
          <p className='text-grey40'>Taxed at a flat 12.5%</p>
          <h4 className='text-[14px] font-normal text-[#3D3D3D] pt-2'>For units purchased on or after 1st Apr 2023</h4>
          <p className='text-grey40'>Taxed as per your income tax slab</p>
        </section>
      </div>
    </BottomSheetContent>
  )
}
